import fetch from './fetch';
import { camelizeKeys, decamelizeKeys } from 'humps'

const logResponse = (response) => {
  if (process.env.NODE_ENV === 'development') {
    console.error(response);
  }
}

const decodeResponse = async (response) => {
  const text = await response.text();
  try {
    const json = JSON.parse(text);
    return typeof(json) === 'object' ? camelizeKeys(json) : json;
  } catch (error) {
    return text;
  }
}

const checkStatus = (response) => {
  if (response.status >= 200 && response.status < 300) {
    return response;
  } else {
    var error = new Error(response.statusText);
    return decodeResponse(response).then(parsedText => {
      error.response = parsedText;
      logResponse(parsedText);
      throw error;
    })
  }
};

export default class Api {
  constructor() {
    this.baseUrl = `${process.env.AMAZY_BASE_URL}/api/admin`;
  };

  fetchCompanyInfo = () => {
    return this.request('GET', '/organisation');
  };

  fetchOrganisations = () => {
    return this.request('GET', '/organisations');
  };

  fetchOrganisation = (id) => {
    return this.request('GET', `/organisations/${id}`);
  };

  createOrganisation = (attrs) => {
    return this.request('POST', `/organisations`, decamelizeKeys({ organisation: attrs }));
  };

  updateOrganisation = (attrs) => {
    return this.request('PUT', `/organisations/${attrs.id}`, decamelizeKeys({ organisation: attrs }));
  };

  fetchTransactions = () => {
    return this.request('GET', '/transactions');
  };

  fetchInvoices = () => {
    return this.request('GET', '/invoices');
  };

  fetchQuotes = (orgId) => {
    return this.request('GET', `/organisations/${orgId}/quotes`)
  };

  calculateQuote = (params) => {
    return this.request('POST', '/quote', decamelizeKeys(params));
  };

  bindQuote = (orgId, quoteUuid, applicationNumber=null) => {
    const params = { organisationId: orgId, quoteUuid, applicationNumber };
    return this.request('POST', '/quote/bind', decamelizeKeys(params));
  }

  fetchLoansByOrg = (orgId) => {
    return this.request('GET', `/organisations/${orgId}/loans`)
  };

  fetchLoans = () => {
    return this.request('GET', '/loans');
  };

  fetchLoan = (loanId) => {
    return this.request('GET', `/loans/${loanId}`);
  };

  fetchInvestors = () => {
    return this.request('GET', '/investors');
  };

  fetchInvestor = (id) => {
    return this.request('GET', `/investors/${id}`);
  };

  createInvestor = (attrs) => {
    return this.request('POST', `/investors`, decamelizeKeys({ investor: attrs }));
  };

  updateInvestor = (attrs) => {
    return this.request('PUT', `/investors/${attrs.id}`, decamelizeKeys({ investor: attrs }));
  };

  createInvestment = (attrs) => {
    return this.request('POST', `/investments`, decamelizeKeys({ investment: attrs }));
  };

  updateInvestment = (attrs) => {
    return this.request('PUT', `/investments/${attrs.id}`, decamelizeKeys({ investment: attrs }));
  };

  fetchReferrals = () => {
    return this.request('GET', '/referrals');
  };

  fetchReferral = (id) => {
    return this.request('GET', `/referrals/${id}`);
  };

  createReferral = (attrs) => {
    return this.request('POST', `/referrals`, decamelizeKeys({ referral: attrs }));
  };

  updateReferral = (attrs) => {
    return this.request('PUT', `/referrals/${attrs.id}`, decamelizeKeys({ referral: attrs }));
  };

  updateLoan = (loanId, payload) => (this.request('PATCH', `/loans/${loanId}`, decamelizeKeys(payload)))

  fetchLoanReminders = () => {
    return this.request('GET', '/loan_schedule/reminder')
  }

  fetchApplications = () => {
    return this.request('GET', '/applications');
  };

  fetchApplication = (appNumber) => {
    return this.request('GET', `/applications/${appNumber}`);
  };

  submitApplication = (appNumber, form) => {
    const params = { form };

    return this.request('PATCH', `/applications/${appNumber}`, decamelizeKeys(params));
  };

  requestDoc = (appNumber, request_doc) => {
    return this.request('PUT', `/applications/${appNumber}/request_doc`, decamelizeKeys({request_doc}));
  };

  approveApplication = (appNumber) => {
    return this.request('PUT', `/applications/${appNumber}/approve`);
  };

  rejectApplication = (appNumber) => {
    return this.request('PUT', `/applications/${appNumber}/reject`);
  };

  authorizeApplication = ({appNumber, authorized}) => {
    return this.request('PUT', `/applications/${appNumber}/authorize`, decamelizeKeys({authorized}));
  };

  confirmRepayment = (loanScheduleId, status) => {
    return this.request('POST', `/confirm_repayments`, decamelizeKeys({loanScheduleId, status}));
  };

  updateRepayment = (repaymentId, status) => {
    return this.request('PATCH', `/repayments/${repaymentId}`, decamelizeKeys({status}));
  };

  fetchPaywayTransactions = () => {
    return this.request('GET', '/payway_transactions');
  };

  noteLoanSchedule = (loanScheduleId, note) => {
    return this.request('POST', `/repayments/note`, decamelizeKeys({loanScheduleId, note}));
  };

  notifyCustomer = (loanScheduleId) => {
    return this.request('POST', `/repayments/notify_customer`, decamelizeKeys({loanScheduleId}));
  }

  fetchXeroSnapshotByOrgId = (orgId) => {
    return this.request('GET', `/organisations/${orgId}/xero_snapshot`);
  }

  request = (method, suffixUrl, data) => {
    return fetch(
      `${this.baseUrl}${suffixUrl}`,
      {
        method,
        body: JSON.stringify(data),
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      },
    )
      .then(checkStatus)
      .then(decodeResponse);
  };
}



// WEBPACK FOOTER //
// ./src/utils/api.js